import { useEffect, useState } from "preact/hooks";
import { EARLY_ACCESS_HREF, get, html, IN_APP, MARKETING_URL, post } from "../lib.js";
import { Field, LangToggle, Logo, Toaster, useAction, useForm } from "../ui.js";
import { getLang, tr } from "../i18n.js";
import { PageGuide } from "../guide.js";
import { newAccountOffer } from "../auth-offer.js";

export function AuthPage({ onDone }) {
  const [mode, setMode] = useState("login");
  const [signupEnabled, setSignupEnabled] = useState(false);
  useEffect(() => {
    // relayza.company's "Start a free trial" buttons link to /?signup, straight to the form.
    const wantsSignup = new URLSearchParams(location.search).has("signup");
    get("/auth/config")
      .then((c) => {
        setSignupEnabled(!!c.signup_enabled);
        if (c.signup_enabled && wantsSignup && !IN_APP) setMode("signup");
      })
      .catch(() => {});
  }, []);
  const { values, bind } = useForm({ email: "", password: "", name: "", business_name: "", timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
  const [busy, run] = useAction();
  const [sentTo, setSentTo] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (mode === "forgot") {
      await run(() => post("/auth/forgot", { email: values.email, lang: getLang() }));
      setSentTo(values.email);
      return;
    }
    if (mode === "login") await run(() => post("/auth/login", { email: values.email, password: values.password }));
    else await run(() => post("/auth/signup", values));
    onDone();
  };
  const offer = newAccountOffer({ inApp: IN_APP, signupEnabled });
  const showLogin = () => {
    setSentTo(null);
    setMode("login");
  };
  return html`<div class="auth-page">
    <div class="ambient a1" /><div class="ambient a2" /><div class="grid-surface" />
    <div class="auth">
      <div class="auth-pitch">
        ${IN_APP ? html`<${Logo} big />` : html`<a href=${MARKETING_URL} aria-label=${tr("RELAYZA website")}><${Logo} big /></a>`}
        <div class="eyebrow"><span class="pill-dot" /> ${tr("AI FRONT DESK")}</div>
        <h1>${tr("Your business.")}<br /><span class="gradient-text">${tr("Always on.")}</span></h1>
        <p>${tr("Answers every inquiry in seconds, books into your real calendar, sends reminders and follows up, 24/7. Your team steps in whenever a person is needed.")}</p>
        <ul class="checks">
          <li>${tr("WhatsApp, Instagram, Messenger and website chat in one inbox")}</li>
          <li>${tr("Books with live availability from Google or Outlook calendars")}</li>
          <li>${tr("Reminders that cut no-shows, follow-ups that recover leads")}</li>
        </ul>
      </div>
      ${mode === "forgot"
        ? html`<form class="card auth-card" onSubmit=${submit}>
            <h2>${tr("Reset your password")}</h2>
            ${sentTo
              ? html`<p>${tr("If {email} has an account, a link to choose a new password is on its way. It works once, within an hour.", { email: sentTo })}</p>
                  <p class="muted small">${tr("Nothing arrived? Check spam, or ask again in a few minutes.")}</p>`
              : html`<p class="muted">${tr("Enter your email and we'll send you a link to choose a new password.")}</p>
                  <${Field} label=${tr("Email")}><input type="email" required ...${bind("email")} autocomplete="email" /></${Field}>
                  <button class="btn primary full" disabled=${busy}>${busy ? tr("Please wait…") : tr("Send reset link")}</button>`}
            <p class="muted center"><button type="button" class="link" onClick=${showLogin}>${tr("Back to sign in")}</button></p>
            <p class="muted center small"><${LangToggle} /></p>
          </form>`
        : html`<form class="card auth-card" onSubmit=${submit}>
        <h2>${mode === "login" ? tr("Sign in") : tr("Create your business account")}</h2>
        ${mode === "signup" && html`
          <${Field} label=${tr("Your name")}><input required ...${bind("name")} autocomplete="name" /></${Field}>
          <${Field} label=${tr("Business name")}><input required ...${bind("business_name")} /></${Field}>`}
        <${Field} label=${tr("Email")}><input type="email" required ...${bind("email")} autocomplete="email" /></${Field}>
        <${Field} label=${tr("Password")} hint=${mode === "signup" ? tr("At least 8 characters") : null}>
          <input type="password" required minlength=${mode === "signup" ? 8 : 1} ...${bind("password")} autocomplete=${mode === "login" ? "current-password" : "new-password"} />
        </${Field}>
        ${mode === "login" && html`<p class="small forgot-row"><button type="button" class="link" onClick=${() => setMode("forgot")}>${tr("Forgot password?")}</button></p>`}
        <button class="btn primary full" disabled=${busy}>${busy ? tr("Please wait…") : mode === "login" ? tr("Sign in") : tr("Create account")}</button>
        ${mode !== "login"
          ? html`<p class="muted center">${tr("Already have an account?")} <button type="button" class="link" onClick=${() => setMode("login")}>${tr("Sign in")}</button></p>`
          : offer === "signup"
          ? html`<p class="muted center">${tr("New here?")} <button type="button" class="link" onClick=${() => setMode("signup")}>${tr("Create an account")}</button></p>`
          : offer === "early_access"
          ? html`<p class="muted center">${tr("New here?")} <a href=${EARLY_ACCESS_HREF}>${tr("Request early access")}</a></p>`
          : null}
        <p class="muted center small"><${LangToggle} /></p>
      </form>`}
      ${!IN_APP && html`<${PageGuide} id="auth" />`}
    </div>
    <${Toaster} />
  </div>`;
}

/** Where the emailed link lands: choose a new password, then sign in with it everywhere. */
export function ResetPage() {
  const params = new URLSearchParams(location.search);
  const token = params.get("token") ?? "";
  // An invite lands here too: same link, a welcome instead of "reset".
  const invite = params.get("invite") === "1";
  const { values, bind } = useForm({ password: "", confirm: "" });
  const [busy, run] = useAction();
  const [done, setDone] = useState(false);
  const mismatch = values.confirm.length > 0 && values.password !== values.confirm;
  const submit = async (e) => {
    e.preventDefault();
    if (values.password !== values.confirm) return;
    await run(() => post("/auth/reset", { token, password: values.password }));
    setDone(true);
  };
  const toSignIn = () => {
    // A full load, so the dashboard asks the server afresh who is signed in (nobody, after a reset).
    location.assign("/");
  };
  return html`<div class="auth-page">
    <div class="ambient a1" /><div class="ambient a2" /><div class="grid-surface" />
    <div class="auth auth-single">
      <form class="card auth-card" onSubmit=${submit}>
        <a href=${MARKETING_URL} aria-label=${tr("RELAYZA website")}><${Logo} /></a>
        <h2>${invite ? tr("Welcome to RELAYZA") : tr("Choose a new password")}</h2>
        ${invite && !done && token && html`<p class="muted small">${tr("Choose a password, then sign in with your email address and it.")}</p>`}
        ${!token
          ? html`<p>${tr("This link is incomplete. Open the link from the email again, or ask for a new one.")}</p>
              <button type="button" class="btn primary full" onClick=${toSignIn}>${tr("Back to sign in")}</button>`
          : done
          ? html`<p>${invite ? tr("Your password is set. Sign in with your email address and the new password.") : tr("Your password is changed. You were signed out everywhere, the app included, so sign in again with the new one.")}</p>
              <button type="button" class="btn primary full" onClick=${toSignIn}>${tr("Sign in")}</button>`
          : html`<${Field} label=${tr("New password")} hint=${tr("At least 8 characters")}>
                <input type="password" required minlength="8" ...${bind("password")} autocomplete="new-password" />
              </${Field}>
              <${Field} label=${tr("Repeat the new password")} hint=${mismatch ? tr("The two passwords don't match") : null}>
                <input type="password" required minlength="8" ...${bind("confirm")} autocomplete="new-password" />
              </${Field}>
              <button class="btn primary full" disabled=${busy || mismatch}>${busy ? tr("Please wait…") : tr("Save new password")}</button>`}
        <p class="muted center small"><${LangToggle} /></p>
      </form>
    </div>
    <${Toaster} />
  </div>`;
}
